import React, { HTMLAttributes } from "react";
import { animated, useInView, useSpring, useTrail } from "react-spring";
import { twMerge } from "tailwind-merge";
import BaseButton from "../../atoms/baseButton";
import SectionTitle from "../../atoms/sectionTitle";

interface IEmergencyChargingSupportSection {}

type TEmergencyChargingSupportSectionProps = IEmergencyChargingSupportSection &
  HTMLAttributes<HTMLDivElement>;

const EmergencyChargingSupportSection: React.FC<
  TEmergencyChargingSupportSectionProps
> = (props) => {
  const { ...otherProps } = props;
  const [ref, inView] = useInView({
    amount: 0.35,
    once: false,
  });

  // Image "powers on" — soft zoom + brightness (not JoinAsgard shutter bars)
  const imageSpring = useSpring({
    opacity: inView ? 1 : 0,
    scale: inView ? 1 : 0.9,
    filter: inView
      ? "brightness(1) saturate(1)"
      : "brightness(0.35) saturate(0.6)",
    boxShadow: inView
      ? "0 0 32px 2px rgba(80, 189, 236, 0.28)"
      : "0 0 0 0 rgba(80, 189, 236, 0)",
    config: { tension: 140, friction: 22 },
  });

  // Cyan charge sweep left → right across the photo
  const sweepSpring = useSpring({
    transform: inView ? "translateX(420%)" : "translateX(0%)",
    opacity: inView ? 0 : 0.9,
    config: { duration: inView ? 1000 : 0 },
    delay: inView ? 160 : 0,
  });

  // Copy rises in staggered steps — distinct from JoinAsgard's right-slide
  const textTrail = useTrail(3, {
    opacity: inView ? 1 : 0,
    y: inView ? 0 : 48,
    config: { tension: 210, friction: 26 },
    delay: inView ? 280 : 0,
  });

  const buttonSpring = useSpring({
    opacity: inView ? 1 : 0,
    y: inView ? 0 : 28,
    scale: inView ? 1 : 0.88,
    config: { tension: 260, friction: 18 },
    delay: inView ? 620 : 0,
  });
  return (
    <div
      className="justify-center items-center flex flex-col p-8 laptop:py-10 laptop:px-[10%] overflow-hidden"
      {...otherProps}
    >
      <SectionTitle
        chineseTitle="24/7緊急充電支援服務"
        englishTitle="24/7 Emergency Charging Support"
      />

      {/* items-center: right column stays a compact cluster beside the image
          — do NOT stretch with h-full / justify-between or the CTA pins to the bottom */}
      <div
        ref={ref}
        className="flex flex-col laptop:flex-row gap-6 laptop:gap-12 items-center justify-center w-full"
      >
        <animated.div
          className="relative w-full laptop:w-1/2 overflow-hidden rounded-xl"
          style={imageSpring}
        >
          <img
            src={require("../../../assets/images/emergencyCar.png")}
            className="aspect-video w-full object-cover"
            alt="emergency-charging-support-car"
          />
          <animated.div
            aria-hidden
            className="pointer-events-none absolute top-0 bottom-0 left-[-30%] w-[28%] skew-x-[-18deg]"
            style={{
              ...sweepSpring,
              background:
                "linear-gradient(90deg, transparent, rgba(80,189,236,0.55), transparent)",
            }}
          />
        </animated.div>

        {/* Compact stack: title → body → button. Button follows copy, not image bottom. */}
        <div className="flex w-full laptop:w-1/2 flex-col text-white text-sm laptop:text-base gap-4">
          <animated.span style={textTrail[0]} className={twMerge("font-bold")}>
            <span className="text-2xl">緊急沒電？</span>
            <span> Out of Power?</span>
          </animated.span>

          <animated.span style={textTrail[1]}>
            不論您在停車場、路邊還是偏遠地區遇到車輛沒電，我們的「緊急流動充電服務」都能讓您迅速重獲動力！我們擁有專業的流動支援專車，24小時全天候候命，即時將電力直接送到您的位置。
          </animated.span>

          <animated.span
            style={textTrail[2]}
            className="text-sm laptop:text-base"
          >
            Whether you are stranded in a car park, on the roadside, or in a
            remote area with a depleted battery, our Emergency Mobile Charging
            Service gets you moving again! With our dedicated EV support vehicle
            operating 24/7, we deliver instant charging directly to your
            location.
          </animated.span>

          <animated.a
            href="https://247asgard.com.hk"
            target="_blank"
            rel="noreferrer"
            style={buttonSpring}
            className="pt-1 block w-full laptop:w-[10.75rem]"
          >
            <BaseButton className="w-full">
              <div className="flex flex-col text-white">
                <span>了解詳情</span>
                <span>More Details</span>
              </div>
            </BaseButton>
          </animated.a>
        </div>
      </div>
    </div>
  );
};

export default EmergencyChargingSupportSection;
